// Configuration-app modal — the configurable-endpoints pattern's opener.
//
// A pipeline node whose params carry `config_view` (a fork-local *_editor.view
// endpoint) opens its CONFIGURATION APP here, in a modal over the canvas —
// config apps are deliberately NOT listed on the Apps page (the backend
// filters *_editor.view out of /api/views and the app registry sync), because
// they configure ONE node of ONE pipeline, not a standalone product surface.
//
// The app itself is an ordinary fork view rendered by the embedded studio
// (/flow-embed …?autorunView=1 — same mechanism as Apps.jsx), so it can call
// fork endpoints over rpc$ AND, being same-origin, the cortex REST API.
// Node context (which node/source/target the app configures) is passed in the
// iframe URL HASH — the view reads location.hash, nothing is baked in.
import React from "react";
import { X, Settings2 } from "lucide-react";

export default function ConfigAppModal({ node, configView, forkProjectId, project, onClose }) {
  const hash = new URLSearchParams({
    project: project || "",
    node: node?.id != null ? String(node.id) : "",
    source: node?.data_source_id != null ? String(node.data_source_id) : "",
    target: node?.data_target_id != null ? String(node.data_target_id) : "",
    kind: node?.kind || "",
  }).toString();
  const src = forkProjectId && configView
    ? `/flow-embed/projects/${forkProjectId}/endpoints/${encodeURIComponent(configView)}/flow?autorunView=1#${hash}`
    : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-6"
      onClick={onClose}
      data-testid="config-app-modal"
    >
      <div
        className="bg-card-bg rounded-[10px] border border-card-border w-full max-w-6xl h-[88vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-card-border shrink-0">
          <h2 className="text-base font-medium text-page-title inline-flex items-center gap-2">
            <Settings2 size={15} className="text-page-muted" />
            {node?.label || "Configuration"}
            <code className="text-[11px] text-page-muted font-normal">{configView}</code>
          </h2>
          <button onClick={onClose} className="text-page-muted hover:text-page-text border-0 bg-transparent cursor-pointer p-1" aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 min-h-0 bg-[var(--page-bg)]">
          {src ? (
            <iframe
              data-testid="config-app-iframe"
              src={src}
              title={`Configuration: ${configView}`}
              className="w-full h-full border-0"
            />
          ) : (
            <div className="p-6 text-page-muted text-sm">This node names no configuration app.</div>
          )}
        </div>
      </div>
    </div>
  );
}
